import React, { useState, useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Check, ShieldCheck, ChevronRight } from "lucide-react";
import { findProduct, getBrand } from "@/lib/products";
import { Button } from "@/components/ui/button";
import { useCart } from "@/context/CartContext";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";

const SCRIPT_ID = "product-jsonld";

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const product = findProduct(id);
  const { addItem } = useCart();
  const [selectedPlan, setSelectedPlan] = useState(product?.plans[0]?.id);

  useDocumentMeta({
    title: product ? `${product.name} — Buy at Bulk Price` : "Product",
    description: product
      ? `${product.name}: ${product.tagline}. ${product.description.slice(0, 140)}`
      : "Huntheal products at bulk prices.",
  });

  // Inject Product JSON-LD for SEO (Google Merchant + rich result)
  useEffect(() => {
    if (!product) return;
    const minPrice = Math.min(...product.plans.map((p) => p.price));
    const maxPrice = Math.max(...product.plans.map((p) => p.price));
    const data = {
      "@context": "https://schema.org",
      "@type": "Product",
      name: product.name,
      description: product.description,
      brand: { "@type": "Brand", name: getBrand(product) },
      category: product.category,
      image: product.image,
      sku: product.id,
      offers: {
        "@type": "AggregateOffer",
        priceCurrency: "USD",
        lowPrice: minPrice.toFixed(2),
        highPrice: maxPrice.toFixed(2),
        offerCount: product.plans.length,
        availability: "https://schema.org/InStock",
        seller: { "@type": "Organization", name: "ProsoftMarketplace" },
      },
      aggregateRating: {
        "@type": "AggregateRating",
        ratingValue: "4.8",
        reviewCount: "12000",
      },
    };
    let s = document.getElementById(SCRIPT_ID);
    if (!s) {
      s = document.createElement("script");
      s.id = SCRIPT_ID;
      s.type = "application/ld+json";
      document.head.appendChild(s);
    }
    s.textContent = JSON.stringify(data);
    return () => {
      const existing = document.getElementById(SCRIPT_ID);
      if (existing) existing.remove();
    };
  }, [product]);

  if (!product) {
    return (
      <div className="mx-auto max-w-4xl px-4 py-24 text-center">
        <h1 className="font-display text-3xl font-bold">Product not found</h1>
        <Button asChild className="mt-6 rounded-sm bg-emerald-500 text-slate-900 hover:bg-emerald-400">
          <Link to="/products">Browse all products</Link>
        </Button>
      </div>
    );
  }

  const plan = product.plans.find((p) => p.id === selectedPlan) || product.plans[0];
  const discount = Math.round((1 - plan.price / plan.original) * 100);

  return (
    <div data-testid={`product-detail-${product.id}`}>
      <div className="border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-7xl px-4 py-4 sm:px-6 lg:px-8">
          <Link
            to="/products"
            className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-[0.2em] text-slate-500 hover:text-slate-900"
            data-testid="back-to-products"
          >
            <ArrowLeft className="h-3 w-3" /> Back to products
          </Link>
        </div>
      </div>

      <div className="mx-auto grid max-w-7xl gap-12 px-4 py-12 sm:px-6 lg:grid-cols-12 lg:px-8 lg:py-16">
        {/* Image */}
        <div className="lg:col-span-6">
          <div className="relative">
            <img
              src={product.image}
              alt={product.name}
              className="w-full border border-slate-900 object-cover"
            />
            <div className="absolute -bottom-4 -right-4 -z-10 h-full w-full bg-emerald-500" />
          </div>
          <div className="mt-6 grid grid-cols-3 gap-3 text-xs">
            {[
              { l: "Devices", v: product.devices },
              { l: "Category", v: product.category },
              { l: "Updated", v: "2026" },
            ].map((m) => (
              <div key={m.l} className="border border-slate-200 p-3">
                <div className="font-bold uppercase tracking-[0.2em] text-slate-500">{m.l}</div>
                <div className="mt-1 font-display text-base font-black text-slate-900">{m.v}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Info */}
        <div className="lg:col-span-6">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">
            <span className="bg-slate-900 px-1.5 py-0.5 text-[10px] font-black tracking-[0.18em] text-emerald-500">
              {getBrand(product)}
            </span>
            <span className="ml-2">{product.category}</span>
          </div>
          <h1 className="mt-2 font-display text-4xl font-black tracking-tighter text-slate-900 sm:text-5xl">
            {product.name}
          </h1>
          <p className="mt-3 text-lg text-slate-600">{product.tagline}</p>
          <p className="mt-4 text-sm leading-relaxed text-slate-700">{product.description}</p>

          {/* Plan selector */}
          <div className="mt-8">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">
              Select your plan
            </div>
            <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
              {product.plans.map((p) => {
                const selected = selectedPlan === p.id;
                const off = Math.round((1 - p.price / p.original) * 100);
                return (
                  <button
                    key={p.id}
                    onClick={() => setSelectedPlan(p.id)}
                    data-testid={`plan-option-${p.id}`}
                    className={`relative border p-4 text-left transition-all ${
                      selected
                        ? "border-slate-900 bg-slate-900 text-white"
                        : "border-slate-300 bg-white hover:border-slate-900"
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <div className={`text-xs font-bold uppercase tracking-[0.2em] ${selected ? "text-emerald-500" : "text-slate-500"}`}>
                          {p.label}
                        </div>
                        <div className="mt-2 font-display text-2xl font-black tracking-tighter">
                          ${p.price.toFixed(2)}
                        </div>
                        <div className={`text-xs ${selected ? "text-slate-300" : "text-slate-500"}`}>
                          <span className="line-through">${p.original.toFixed(2)}</span>
                          <span className={`ml-1 font-bold ${selected ? "text-emerald-500" : "text-emerald-600"}`}>
                            Save {off}%
                          </span>
                        </div>
                      </div>
                      {selected && (
                        <span className="flex h-6 w-6 items-center justify-center bg-emerald-500">
                          <Check className="h-4 w-4 text-slate-900" strokeWidth={3} />
                        </span>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="mt-8 flex flex-col gap-3 sm:flex-row">
            <Button
              data-testid="detail-add-to-cart"
              onClick={() => addItem(product.id, plan.id, 1)}
              className="flex-1 rounded-sm bg-slate-900 py-6 text-base font-bold text-white hover:bg-slate-800"
            >
              Add to Cart — ${plan.price.toFixed(2)}
            </Button>
            <Button
              data-testid="detail-buy-now"
              onClick={() => {
                addItem(product.id, plan.id, 1);
                navigate("/checkout");
              }}
              className="flex-1 rounded-sm bg-emerald-500 py-6 text-base font-bold text-slate-900 hover:bg-emerald-400"
            >
              Buy Now <ChevronRight className="ml-1 h-4 w-4" />
            </Button>
          </div>

          <div className="mt-4 flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-slate-600">
            <ShieldCheck className="h-4 w-4 text-emerald-500" />
            30-day money-back guarantee · {discount}% off today
          </div>
        </div>
      </div>

      {/* Features list */}
      <section className="border-y border-slate-200 bg-slate-50">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
            What&apos;s included
          </h2>
          <ul className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {product.features.map((f, i) => (
              <li key={i} className="flex items-start gap-3 border border-slate-200 bg-white p-4">
                <span className="flex h-7 w-7 shrink-0 items-center justify-center bg-emerald-500">
                  <Check className="h-4 w-4 text-slate-900" strokeWidth={3} />
                </span>
                <span className="text-sm text-slate-800">{f}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>
    </div>
  );
};

export default ProductDetail;
